import { Pipe, PipeTransform } from '@angular/core';

import { MICROS_PER_UNIT } from 'src/app/models/Wallet';

/**
 * Formata dinheiro em micros (1 BRL = 1.000.000) para leitura.
 *
 * Por que não o `numberFormat` existente: ele recebe valor já "humano", exige o
 * símbolo passado à mão em cada template e tem o formato pt-BR hardcoded por
 * regex. A carteira entrega inteiro em micros e vai operar em mais de uma moeda,
 * então símbolo e separadores têm de sair da moeda + locale, não do template.
 *
 * ## Casas decimais — três escalas, decisão do Bruno em 18/09/2026
 *
 * O default do pipe (mínimo 2, máximo 6) é a precisão crua dos micros: o mínimo
 * é para dinheiro ler como dinheiro (`R$ 1,50`, não `R$ 1,5`) e o máximo é o que
 * os micros comportam. Ele serve o `title` dos cards e as telas de preço, onde o
 * valor exato é o ponto.
 *
 * Nas telas que o cliente lê, a escala é fixa — mínimo igual ao máximo, para a
 * coluna alinhar e o mesmo valor não mudar de largura entre linhas:
 *
 * - **Card de saldo e gasto por canal** (`MONEY_BALANCE_FRACTION_DIGITS`, 2):
 *   `R$ 161,151564` não lê como dinheiro, e o número que o cliente precisa
 *   reconhecer é o saldo, não a soma dele.
 * - **Extrato** (`MONEY_ENTRY_FRACTION_DIGITS`, 3): 2 casas apagariam o débito
 *   de IA — ~R$ 0,0007 por mensagem viraria `R$ 0,00`, um lançamento que tirou
 *   saldo aparecendo como zero — e as 6 casas anteriores davam à linha uma cara
 *   de dízima. Em 3 casas o sub-centavo continua visível (`R$ 0,001`).
 *
 * Riscos aceitos, e são decisão, não descuido: somando os lançamentos à mão o
 * cliente pode não fechar com o card, e débito abaixo de meio milésimo ainda sai
 * como `R$ 0,000` no extrato (`R$ 0,00` no gasto por canal). O valor cheio fica
 * no `title` de quem tem `title`; se virar chamado, a saída é dar visibilidade a
 * esse valor, não devolver as 6 casas ao número da tela.
 */
export const MONEY_MIN_FRACTION_DIGITS = 2;
export const MONEY_MAX_FRACTION_DIGITS = 6;

/** Casas do card de saldo e do gasto por canal. Ver "Casas decimais" acima. */
export const MONEY_BALANCE_FRACTION_DIGITS = 2;

/** Casas do lançamento no extrato. Ver "Casas decimais" acima. */
export const MONEY_ENTRY_FRACTION_DIGITS = 3;

/** O painel é em português: os separadores são pt-BR mesmo quando a moeda não é BRL. */
export const MONEY_DEFAULT_LOCALE = 'pt-BR';

const CURRENCY_CODE_PATTERN = /^[A-Za-z]{3}$/;

@Pipe({ name: 'moneyMicros' })
export class MoneyMicrosPipe implements PipeTransform {

  transform(
    micros: number | string | null | undefined,
    currency: string = 'BRL',
    locale: string = MONEY_DEFAULT_LOCALE,
    maxFractionDigits: number = MONEY_MAX_FRACTION_DIGITS,
    minFractionDigits: number = MONEY_MIN_FRACTION_DIGITS
  ): string {
    const amount = Number(micros);

    // Ausência não é zero: zero é uma resposta verdadeira da carteira, e um card
    // vazio é menos errado do que um `R$ 0,00` inventado.
    if (micros === null || micros === undefined || micros === '' || !isFinite(amount)) {
      return '';
    }

    const value = amount / MICROS_PER_UNIT;

    // Centavo é o piso: nem o mínimo nem o teto descem abaixo dele, senão
    // `R$ 1,50` viraria `R$ 1,5` ou `R$ 2` numa escala mal passada.
    const minimumFractionDigits = Math.max(minFractionDigits, MONEY_MIN_FRACTION_DIGITS);
    const maximumFractionDigits = Math.max(maxFractionDigits, minimumFractionDigits);
    const targetLocale = locale || MONEY_DEFAULT_LOCALE;

    // Moeda fora do padrão ISO faria o Intl lançar RangeError. Cair para BRL
    // mentiria sobre a moeda, então o número sai formatado e o código vem na
    // frente como ele é.
    if (!CURRENCY_CODE_PATTERN.test(currency || '')) {
      const decimal = new Intl.NumberFormat(targetLocale, {
        minimumFractionDigits,
        maximumFractionDigits,
      }).format(value);

      return currency ? `${currency} ${decimal}` : decimal;
    }

    return new Intl.NumberFormat(targetLocale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);
  }
}
